/**
 * CRM y comunicación — AF §10 (RN-CRM-01 a RN-CRM-18).
 *
 * Fuente única de las formas que comparten backend, portal admin y app de socios.
 * Convención del contrato §0: Zod en el 100 % de los endpoints; `Money` en centavos;
 * las listas devuelven `{ items, total, page, limit }`.
 */
import { z } from "zod";
import { CAMPAIGN_STATUSES, COMMENT_MODERATION_ACTIONS, CONTACT_PERSON_KINDS, CONVERSATION_STATUSES, MODERATION_ACTIONS, NEWS_STATUSES, NOTIFICATION_CHANNELS, POST_STATUSES, PROSPECT_STATUSES, REPORT_REASONS, RETENTION_SEGMENTS, } from "../enums.js";
import { dateISO, listQuery, objectId, pagination, reasonRequired } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Interesados (RN-CRM-01..06)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-CRM-02: el origen es OBLIGATORIO y sale del catálogo `contact_sources`.
 * `referrerMemberId` es el socio recomendante — opcional, y sólo tiene sentido con
 * `code: "referral"`.
 */
export const prospectSource = z.object({
    code: z.string().trim().min(2).max(40),
    referrerMemberId: objectId.optional(),
});
export const createProspectBody = z.object({
    name: z.string().trim().min(2).max(120),
    phone: z.string().trim().min(6).max(40),
    email: z.string().trim().toLowerCase().email().optional(),
    instagram: z.string().trim().max(80).optional(),
    /** Para el dedupe por documento contra socios e interesados (RN-CRM-06). */
    docNumber: z.string().trim().min(5).max(20).optional(),
    branchInterestId: objectId.optional(),
    activityInterestId: objectId.optional(),
    /** Responsable comercial de la ficha; las tareas pueden tener responsables propios. */
    assigneeUserId: objectId.optional(),
    source: prospectSource,
    notes: z.string().trim().max(2000).optional(),
    nextContactAt: dateISO.optional(),
});
export const updateProspectBody = createProspectBody.partial().omit({ source: true }).extend({
    source: prospectSource.optional(),
});
/**
 * RN-CRM-03: los estados del pipeline. `converted` no retrocede (lo valida el servicio) y
 * `lost` exige motivo del catálogo `lost_reasons`, que es administrable.
 */
export const setProspectStatusBody = z
    .object({
    status: z.enum(PROSPECT_STATUSES),
    lostReason: z.string().trim().min(2).max(40).optional(),
})
    .refine((v) => v.status !== "lost" || Boolean(v.lostReason), {
    message: "«Perdido» exige motivo del catálogo (RN-CRM-03)",
    path: ["lostReason"],
});
export const listProspectsQuery = listQuery.extend({
    status: z.enum(PROSPECT_STATUSES).optional(),
    source: z.string().trim().max(40).optional(),
    activityInterestId: objectId.optional(),
    assigneeUserId: objectId.optional(),
    /** Sólo los resaltados «fríos» (RN-CRM-04). */
    cold: z.coerce.boolean().optional(),
    /** Sólo los que tienen una tarea vencida (RN-CRM-04). */
    overdueTasks: z.coerce.boolean().optional(),
    lostReason: z.string().trim().max(40).optional(),
});
/** RN-CRM-04: tarea de seguimiento con fecha y responsable. */
export const prospectTaskBody = z.object({
    title: z.string().trim().min(3).max(200),
    dueAt: dateISO,
    assigneeUserId: objectId,
});
export const completeTaskBody = z.object({
    result: z.string().trim().min(2).max(500),
    /** Si se registra el contacto en el mismo acto (RN-CRM-07/18). */
    channel: z.string().trim().max(40).optional(),
});
/** RN-CRM-18: contacto humano registrado a mano (llamada, WhatsApp externo, presencial). */
export const createContactLogBody = z.object({
    channel: z.string().trim().min(2).max(40),
    result: z.string().trim().min(2).max(1000),
    notes: z.string().trim().max(2000).optional(),
    at: dateISO.optional(),
});
/**
 * RN-CRM-07 / RN-REP-08: contacto de retención registrado desde «No renovados / en
 * riesgo». Quién y la sede los pone el servidor (usuario y sede activa); el resto es lo que
 * anota recepción: cuándo, medio, resultado y, si hace falta, el próximo paso — que se
 * guarda como tarea con fecha y responsable (RN-CRM-04), así entra a la bandeja.
 */
export const retentionContactBody = createContactLogBody.extend({
    /** La tarea de contacto abierta que este contacto cierra (la del reporte). */
    closeTaskId: objectId.optional(),
    nextStep: z
        .object({
        title: z.string().trim().min(3).max(200),
        dueAt: dateISO,
        /** Default: quien registra el contacto. */
        assigneeUserId: objectId.optional(),
    })
        .optional(),
});
/**
 * RN-CRM-05: la clase de prueba se agenda desde la ficha CONTRA LA GRILLA REAL.
 * El DNI es obligatorio: el control de «una sola por persona, de por vida» es por
 * documento (RN-PLA-22).
 */
export const scheduleTrialBody = z.object({
    sessionId: objectId,
    docNumber: z.string().trim().min(5).max(20),
    age: z.number().int().min(1).max(120).optional(),
});
/** RN-CRM-05: resultado posterior obligatorio — vino / no vino. */
export const trialResultBody = z.object({ attended: z.boolean() });
/**
 * RN-CRM-06: conversión a socio. El alta sigue el flujo de D4 (servicio real de Socios),
 * así que el body es el complemento de lo que el interesado ya tiene.
 */
export const convertProspectBody = z.object({
    docType: z.string().trim().max(20).optional(),
    docNumber: z.string().trim().min(5).max(20),
    firstName: z.string().trim().min(2).max(80),
    lastName: z.string().trim().min(2).max(80),
    birthDate: dateISO,
    email: z.string().trim().toLowerCase().email().optional(),
    phone: z.string().trim().min(6).max(40).optional(),
    address: z.string().trim().max(200).optional(),
    branchId: objectId.optional(),
    guardian: z
        .object({
        name: z.string().trim().min(2).max(120),
        dni: z.string().trim().min(5).max(20),
        phone: z.string().trim().min(6).max(40),
        email: z.string().trim().toLowerCase().email().optional(),
    })
        .optional(),
});
/** Frontera con §13 Migración: el Drive de averiguaciones entra por acá (RN-MIG-06). */
export const importProspectRow = z.object({
    name: z.string().trim().min(1).max(120),
    phone: z.string().trim().max(40).default(""),
    email: z.string().trim().toLowerCase().max(120).default(""),
    instagram: z.string().trim().max(80).optional(),
    docNumber: z.string().trim().max(20).optional(),
    sourceCode: z.string().trim().max(40).default("other"),
    status: z.enum(PROSPECT_STATUSES).default("new"),
    lostReason: z.string().trim().max(40).optional(),
    branchInterestId: objectId.optional(),
    notes: z.string().trim().max(2000).optional(),
    createdAt: dateISO.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Retención: tareas, segmentos y campañas (RN-CRM-07..09)
// ─────────────────────────────────────────────────────────────────────────────
export const listTasksQuery = pagination.extend({
    branchId: objectId.optional(),
    assigneeUserId: objectId.optional(),
    personKind: z.enum(CONTACT_PERSON_KINDS).optional(),
    /** Sólo las vencidas: es lo que la bandeja de la sede muestra arriba (RN-CRM-04). */
    overdue: z.coerce.boolean().optional(),
    done: z.coerce.boolean().optional(),
});
export const createTaskBody = z.object({
    person: z.object({ kind: z.enum(CONTACT_PERSON_KINDS), id: objectId }),
    title: z.string().trim().min(3).max(200),
    dueAt: dateISO,
    assigneeUserId: objectId,
    branchId: objectId.optional(),
});
/** RN-CRM-08: el segmento con su parámetro. `days` sólo aplica a `plan_expiring`. */
export const segmentQuery = z.object({
    key: z.enum(RETENTION_SEGMENTS),
    branchId: objectId.optional(),
    /** `plan_expiring`: cuántos días de anticipación. */
    days: z.coerce.number().int().min(1).max(180).optional(),
    /** `lost_prospects`: filtro por motivo del catálogo. */
    lostReason: z.string().trim().max(40).optional(),
});
export const createCampaignBody = z.object({
    name: z.string().trim().min(3).max(120),
    segment: segmentQuery,
    message: z.object({
        title: z.string().trim().min(3).max(120),
        body: z.string().trim().min(3).max(1000),
    }),
    channels: z.array(z.enum(NOTIFICATION_CHANNELS)).min(1),
    scheduledAt: dateISO,
});
export const listCampaignsQuery = pagination.extend({
    status: z.enum(CAMPAIGN_STATUSES).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Catálogo de avisos y centro de avisos (RN-CRM-10/11)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-CRM-10: cada aviso es activable y sus plazos son parámetros. */
export const updateNoticeCatalogEntryBody = z.object({
    active: z.boolean().optional(),
    channels: z.array(z.enum(NOTIFICATION_CHANNELS)).min(1).optional(),
    params: z.record(z.string(), z.number()).optional(),
});
export const listNoticesQuery = pagination.extend({
    unreadOnly: z.coerce.boolean().optional(),
    type: z.string().trim().max(40).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Noticias y muro (RN-CRM-12..14)
// ─────────────────────────────────────────────────────────────────────────────
export const createNewsBody = z.object({
    title: z.string().trim().min(3).max(160),
    body: z.string().trim().min(3).max(10_000),
    imageKeys: z.array(z.string().trim().max(300)).max(10).default([]),
    /** `null` = todo el club; con sede = segmentada (RN-CRM-12). */
    branchScope: objectId.nullable().optional(),
    featured: z.boolean().optional(),
    publishedAt: dateISO.optional(),
});
/**
 * RN-CRM-12: editar una noticia. `status` publica, deja en borrador o despublica; borrar
 * es `DELETE /api/news/:id` (borrado lógico).
 */
export const updateNewsBody = createNewsBody.partial().extend({
    status: z.enum(NEWS_STATUSES).optional(),
});
export const listNewsQuery = pagination.extend({
    branchId: objectId.optional(),
    featured: z.coerce.boolean().optional(),
});
/** Máximo de fotos por publicación del muro. */
export const WALL_MAX_IMAGES = 4;
/**
 * Publicación de un socio en el muro. Nace `pending` si la moderación es previa.
 * `imageKeys`: las keys que devolvió `POST /api/app/uploads/sign` con `purpose: wall_post`
 * (prefijo `wall/<id del socio>/`). `imageKey` (una sola) es la forma vieja: se acepta y
 * se suma a `imageKeys`.
 */
export const createWallPostBody = z
    .object({
    body: z.string().trim().min(1).max(2000),
    imageKeys: z.array(z.string().trim().min(1).max(300)).max(WALL_MAX_IMAGES).default([]),
    imageKey: z.string().trim().min(1).max(300).optional(),
})
    .refine((v) => v.imageKeys.length + (v.imageKey ? 1 : 0) <= WALL_MAX_IMAGES, {
    message: `Una publicación lleva como máximo ${WALL_MAX_IMAGES} fotos`,
    path: ["imageKeys"],
});
/** Paginación por cursor del muro, de «mis publicaciones» y de los comentarios. */
export const wallCursorQuery = z.object({
    cursor: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const wallCommentParams = z.object({ id: objectId, commentId: objectId });
export const wallCommentBody = z.object({ body: z.string().trim().min(1).max(1000) });
export const wallReactionBody = z.object({
    type: z.string().trim().max(20).default("like"),
    /** `false` quita la reacción. */
    on: z.boolean().default(true),
});
/** RN-CRM-13: denuncia con motivo (enum corto) y detalle opcional. */
export const wallReportBody = z.object({
    reason: z.enum(REPORT_REASONS).default("other"),
    detail: z.string().trim().max(500).optional(),
});
/** RN-CRM-14: ocultar, eliminar o restituir un contenido — con motivo, auditado. */
export const moderatePostBody = z
    .object({
    action: z.enum(MODERATION_ACTIONS),
    reason: z.string().trim().max(500).optional(),
})
    .refine((v) => v.action === "approve" || v.action === "restore" || Boolean(v.reason?.trim()), {
    message: "Rechazar, ocultar o eliminar exige motivo (RN-CRM-14)",
    path: ["reason"],
});
/** RN-CRM-14 sobre un comentario: restituir, ocultar o eliminar (los dos últimos con motivo). */
export const moderateCommentBody = z
    .object({
    action: z.enum(COMMENT_MODERATION_ACTIONS),
    reason: z.string().trim().max(500).optional(),
})
    .refine((v) => v.action === "approve" || Boolean(v.reason?.trim()), {
    message: "Ocultar o eliminar un comentario exige motivo (RN-CRM-14)",
    path: ["reason"],
});
/** RN-CRM-14: suspender la capacidad de publicar de un socio, con motivo. */
export const suspendAuthorBody = z.object({
    memberId: objectId,
    days: z.number().int().min(1).max(365),
    reason: reasonRequired,
});
export const moderationQueueQuery = pagination.extend({
    status: z.enum(POST_STATUSES).optional(),
    /** Sólo lo denunciado (la publicación o alguno de sus comentarios). */
    reported: z.coerce.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Mensajería socio↔sede (RN-CRM-15..17)
// ─────────────────────────────────────────────────────────────────────────────
export const listConversationsQuery = pagination.extend({
    branchId: objectId.optional(),
    status: z.enum(CONVERSATION_STATUSES).optional(),
    q: z.string().trim().min(1).max(120).optional(),
});
/** Mensaje del socio: elige la sede (default, la de su alta — RN-CRM-15). */
export const appMessageBody = z.object({
    branchId: objectId.optional(),
    body: z.string().trim().min(1).max(4000),
    attachmentKey: z.string().trim().max(300).optional(),
});
export const replyMessageBody = z.object({
    body: z.string().trim().min(1).max(4000),
    attachmentKey: z.string().trim().max(300).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Línea de tiempo de comunicaciones (RN-CRM-18)
// ─────────────────────────────────────────────────────────────────────────────
export const communicationsParams = z.object({
    kind: z.enum(CONTACT_PERSON_KINDS),
    id: objectId,
});
export const communicationsQuery = pagination.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
});
//# sourceMappingURL=crm.js.map